/* eslint-disable react/no-unescaped-entities */
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Spinner } from 'components/UI';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { Room } from '@spike/room-model';
import { RoomsStatus, getAvailableByPetIdThunk } from '@spike/rooms-action';
import styles from './RoomServiceSelector.module.scss';
import { useApiClientWrapper } from 'hooks';
import SelectRoomServices from './SelectRoomServices';
import { PetsState } from '@spike/pets-action';
import AlertMessage from 'components/UI/V3/AlertMessage';

interface Props {
    petId: number;
    selectedRoom?: Room;
    serviceId?: number;
    dropOffRoom?: string;
    pickUpRoom?: string;
    readOnly?: boolean;
    roomsSelected?: Array<number>;
    multiplePets?: boolean;
    onSelectRoom: (room: Room, data: boolean) => void;
}

export const RoomServiceSelector: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const ref = useRef<HTMLDivElement>(null);

    const roomsData = useSelector<RootState, Array<Room>>(state => state.rooms.roomsAvailableByPetId || []);
    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);
    const petsState = useSelector<RootState, PetsState>(state => state.pets);

    const [loading, setLoading] = useState(true);

    const getRoomsUnused = () => {
        if (props.roomsSelected) {
            const roomsToSelect: Array<Room> = [];
            roomsData.forEach(room => {
                const roomFounded = props.roomsSelected?.find(r => r === room?.id);
                if (!roomFounded) {
                    roomsToSelect.push(room);
                }
            });
            return roomsToSelect;
        }
        return roomsData;
    };

    const pet = useMemo(() => petsState.list.find(p => p.id === props.petId), [props.petId, petsState.list]);

    const petWeight = useMemo(() => {
        if (!pet?.weight) return undefined;
        return Number(pet.weight);
    }, [pet]);

    const conflictingTags = useMemo(() => {
        if (!props.selectedRoom || !pet) return [];

        const petTags = [...(pet.positivePersonalitiesList ?? []), ...(pet.negativePersonalitiesList ?? [])];
        const roomRestrictedTags = props.selectedRoom.restrictions?.petTagIds ?? [];

        return petTags.filter(tag => roomRestrictedTags.includes(tag));
    }, [props.selectedRoom]);

    useEffect(() => {
        setLoading(true);
        dispatch(
            getAvailableByPetIdThunk(
                apiClientWrapper,
                props.petId,
                props.serviceId!,
                props.dropOffRoom!,
                props.pickUpRoom!
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.petId, props.serviceId, props.dropOffRoom, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        if (!props.readOnly) {
            setTimeout(() => {
                ref?.current?.scrollIntoView();
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    useNonInitialEffect(() => {
        switch (roomsStatus) {
            case RoomsStatus.GettingAvailableByPetId:
                setLoading(true);
                break;
            case RoomsStatus.GetAvailableByPetIdSuccess:
                setLoading(false);
                break;
            case RoomsStatus.Error:
                setLoading(false);
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomsStatus]);

    const emptyRoomsComponent = (
        <div id="booking_div_no_rooms_alert">
            <AlertNewBooking
                className={styles.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <span className={styles.alertText}>No rooms available</span>
            </AlertNewBooking>
        </div>
    );

    return (
        <div className={styles.container}>
            {loading ? (
                <Spinner />
            ) : (
                <div ref={ref}>
                    <p className={styles.title}>Rooms & Spaces</p>
                    {getRoomsUnused()?.length === 0 ? (
                        emptyRoomsComponent
                    ) : (
                        <>
                            <SelectRoomServices
                                placeholder="Select a Room"
                                selectedOption={props.selectedRoom}
                                options={getRoomsUnused()}
                                petWeight={petWeight}
                                onSelect={(room: Room, data: boolean) => {
                                    props.onSelectRoom(room, data);
                                }}
                            />
                            {conflictingTags.length > 0 && (
                                <AlertMessage
                                    className={styles.alertConflictingTags}
                                    bWidth="2px"
                                    icon={faExclamationTriangle}
                                    iconColor="#EAB464"
                                    bgColor="#F8F5F1"
                                    bdColor="#BCB8AE"
                                >
                                    <span className={styles.alertText}>
                                        This room isn’t available for pets with tags:{' '}
                                        <b>{conflictingTags.join(', ')}.</b>
                                    </span>
                                </AlertMessage>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RoomServiceSelector;
